import { yupResolver } from '@hookform/resolvers/yup';
import { Button, DynamicFormRequiredType, IconButton } from '@myosh/odin-components';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { type FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import type { CurrentUserPasswordChangeResponse } from '../../../@types/users';
import useTimeoutRef from '../../../hooks/use-timeout-ref';
import i18next from '../../../i18n';
import { useAppDispatch } from '../../../redux/hooks';
import { useChangePasswordMutation, userApi } from '../../../redux/services/user';
import { showError, showSuccess } from '../../../services/notification.service';
import { passwordValidationPattern } from '../../admin/user/user-form-settings';
import HfPasswordField from '../../fields/password-field/hf-password-field.component';

interface ChangePasswordModalProps {
  visible: boolean;
  isFetchingPasswordCheck?: boolean;
  onCloseCallback?: () => void;
  isUserSettingsModal?: boolean;
}

export default function ChangePasswordModal({
  visible,
  onCloseCallback,
  isUserSettingsModal = false,
  isFetchingPasswordCheck,
}: ChangePasswordModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const modalRef = useRef<HTMLDialogElement>(null);
  const openedRef = useRef(false);

  const { t } = useTranslation();
  const { timeoutRef, clearTimeoutRef } = useTimeoutRef();

  const [changePassword] = useChangePasswordMutation();
  const dispatch = useAppDispatch();

  const { control, handleSubmit } = useForm<FieldValues>({
    resolver: yupResolver(schema) as never,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  useEffect(() => {
    if (visible && modalRef.current && !openedRef.current && !isFetchingPasswordCheck) {
      openedRef.current = true;
      modalRef.current.showModal();
    }

    return () => {
      modalRef.current?.close();
    };
  }, [visible, isFetchingPasswordCheck]);

  const handleCancel = useCallback((e: React.SyntheticEvent) => {
    e.preventDefault();
  }, []);

  const onCloseClick = useCallback(() => {
    openedRef.current = false;
    onCloseCallback?.();
    modalRef.current?.close();
  }, [onCloseCallback]);

  const handleClose = useCallback(() => {
    if (isUserSettingsModal) {
      onCloseClick();
    } else {
      clearTimeoutRef();

      timeoutRef.current = setTimeout(() => {
        openedRef.current = false;
      }, 600000); // 10 minutes
    }
  }, [isUserSettingsModal, onCloseCallback]);

  const onSubmit = async (data: FieldValues) => {
    try {
      setLoading(true);

      await changePassword({
        oldPassword: data[CURRENT_PASSWORD],
        newPassword: data[NEW_PASSWORD],
        retypeNewPassword: data[CONFIRM_NEW_PASSWORD],
      }).unwrap();

      openedRef.current = false;
      modalRef.current?.close();
      showSuccess(t('password-change-success'));

      // Invalidate the user cache to refresh the user data after password change
      dispatch(userApi.util.invalidateTags([{ type: 'User' }]));
    } catch (error) {
      const errorResult = error as CurrentUserPasswordChangeResponse;
      if (
        (errorResult.status === 400 || errorResult.status === 403) &&
        errorResult.data.validation?.responseErrors?.[0].message
      ) {
        const errorMessages = errorResult.data.validation.responseErrors.map((error) => error.message).join('\n');
        setError(errorMessages);
      } else {
        openedRef.current = false;
        modalRef.current?.close();
        showError(t('password-change-error'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <dialog
      ref={modalRef}
      onCancel={handleCancel}
      onClose={handleClose}
      className="w-96 rounded-md outline-none backdrop:backdrop-blur-sm"
    >
      <div className="flex items-center justify-between px-4 pb-2 pt-3 text-base font-medium text-mono-0">
        <span>{t('change-password')}</span>
        {isUserSettingsModal && <IconButton onClick={onCloseClick} icon="X" />}
      </div>
      <div className="mt-1 block px-4 text-xs text-error">{error}</div>
      <form method="dialog" onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-8 px-8 py-5">
        <HfPasswordField
          control={control}
          name={CURRENT_PASSWORD}
          label={t(CURRENT_PASSWORD)}
          readOnly={false}
          required={DynamicFormRequiredType.True}
        />
        <HfPasswordField
          control={control}
          name={NEW_PASSWORD}
          label={t(NEW_PASSWORD)}
          readOnly={false}
          required={DynamicFormRequiredType.True}
        />
        <HfPasswordField
          control={control}
          name={CONFIRM_NEW_PASSWORD}
          label={t(CONFIRM_NEW_PASSWORD)}
          readOnly={false}
          required={DynamicFormRequiredType.True}
        />
        <Button type="primary" htmlType="submit" disabled={loading}>
          {t('confirm')}
        </Button>
      </form>
    </dialog>
  );
}

// miscellaneous utils
const CURRENT_PASSWORD = 'current-password';
const NEW_PASSWORD = 'new-password';
const CONFIRM_NEW_PASSWORD = 'confirm-new-password';

const schema = yup.object().shape({
  [CURRENT_PASSWORD]: yup.string().required(i18next.t('required-field', { label: i18next.t(CURRENT_PASSWORD) })),
  [NEW_PASSWORD]: yup
    .string()
    .required(i18next.t('required-field', { label: i18next.t(NEW_PASSWORD) }))
    .matches(passwordValidationPattern, { message: i18next.t('password-validation-message') }),
  [CONFIRM_NEW_PASSWORD]: yup.string().when(NEW_PASSWORD, (value, schema) => {
    return value
      ? schema
          .required(i18next.t('required-field', { label: i18next.t(CONFIRM_NEW_PASSWORD) }))
          .oneOf([yup.ref(NEW_PASSWORD)], i18next.t('confirm-new-password-validation-message'))
      : schema.required(i18next.t('required-field', { label: i18next.t(CONFIRM_NEW_PASSWORD) }));
  }),
});
